import React from 'react';
import { Calendar, User, MessageSquare, Building2, Send, Phone } from 'lucide-react';
import DatePicker from '../DatePicker';
import { formatDateLocal } from '../../utils/dateUtils';
import { B2B_EVENT_TYPES } from '../../config/b2bEventTypes';

export { B2B_EVENT_TYPES };

export const B2B_NEEDS = [
  'Tal & presentation',
  'Panel / moderator',
  'Middag & mingel',
  'Underhållning / DJ',
  'Scen / prisutdelning',
  'Bild / presentation',
  'Ljus / stämning',
  'Streaming / hybrid',
  'Annat',
  'Jag vet inte exakt vad vi behöver – hjälp oss',
] as const;

export const B2B_FULL_SERVICE = [
  'Ja, gärna',
  'Vi vet ungefär vad vi behöver',
  'Osäker',
] as const;

export const B2B_BUDGET = [
  'Under 15 000 kr',
  '15 000–30 000 kr',
  '30 000–50 000 kr',
  '50 000–100 000 kr',
  '100 000+ kr',
  'Vet ej',
] as const;

export type B2bFormData = {
  companyName: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  customerCity: string;
  venue: string;
  eventDate: string;
  eventEndDate: string;
  eventType: string;
  guestCount: string;
  needs: string[];
  fullServicePreference: string;
  budgetSegment: string;
  message: string;
};

type B2bEventFormSectionsProps = {
  formData: B2bFormData;
  setFormData: React.Dispatch<React.SetStateAction<B2bFormData>>;
  errors: Record<string, string>;
  dateUndecided: boolean;
  setDateUndecided: (v: boolean) => void;
  showEndDate: boolean;
  setShowEndDate: (v: boolean) => void;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  markFormStarted: () => void;
  inputClass: (hasError?: boolean) => string;
  loading: boolean;
  submitError?: string;
  showSkaneEventBanner?: boolean;
};

const B2bEventFormSections: React.FC<B2bEventFormSectionsProps> = ({
  formData,
  setFormData,
  errors,
  dateUndecided,
  setDateUndecided,
  showEndDate,
  setShowEndDate,
  setErrors,
  markFormStarted,
  inputClass,
  loading,
  submitError,
  showSkaneEventBanner,
}) => {
  const toggleNeed = (need: string) => {
    markFormStarted();
    setFormData((prev) => {
      const selected = prev.needs.includes(need);
      return {
        ...prev,
        needs: selected ? prev.needs.filter((n) => n !== need) : [...prev.needs, need],
      };
    });
  };

  return (
    <>
      {showSkaneEventBanner && (
        <div className="mb-4 p-3 bg-purple-500/10 border border-purple-500/30 text-sm text-purple-100 leading-relaxed">
          <strong>Förfrågan via Skåne Event.</strong> Festutrustning tar emot och hanterar er
          förfrågan – samma team som driver Skåne Event.
        </div>
      )}

      <div className="mb-4">
        <h3 className="text-sm font-bold text-white mb-2 flex items-center">
          <Calendar className="mr-1.5" size={16} />
          Event
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-400 mb-1">Eventtyp</label>
            <div className="flex flex-wrap gap-1.5">
              {B2B_EVENT_TYPES.map((type) => {
                const selected = formData.eventType === type;
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => {
                      markFormStarted();
                      setFormData({ ...formData, eventType: selected ? '' : type });
                    }}
                    className={`px-2.5 py-1 text-xs border transition-colors ${
                      selected
                        ? 'border-purple-500 bg-purple-600/30 text-white'
                        : 'border-gray-600 text-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {type}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-0.5">Datum</label>
            <DatePicker
              value={dateUndecided ? '' : formData.eventDate}
              onChange={(value) => {
                markFormStarted();
                setDateUndecided(false);
                setFormData({ ...formData, eventDate: value });
              }}
              minDate={formatDateLocal(new Date())}
              error={!!errors.eventDate}
              placeholder={dateUndecided ? 'Datum ej bestämt' : 'Välj datum'}
            />
            <label className="mt-1.5 flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
              <input
                type="checkbox"
                checked={dateUndecided}
                onChange={(e) => {
                  markFormStarted();
                  const checked = e.target.checked;
                  setDateUndecided(checked);
                  if (checked) {
                    setShowEndDate(false);
                    setFormData((prev) => ({ ...prev, eventDate: '', eventEndDate: '' }));
                    setErrors((prev) => {
                      const next = { ...prev };
                      delete next.eventDate;
                      delete next.eventEndDate;
                      return next;
                    });
                  }
                }}
                className="rounded-none border-gray-600"
              />
              Datum ännu inte bestämt
            </label>
            {!dateUndecided && showEndDate && (
              <div className="mt-1.5 max-w-xs">
                <DatePicker
                  value={formData.eventEndDate}
                  onChange={(value) => {
                    markFormStarted();
                    setFormData({ ...formData, eventEndDate: value });
                  }}
                  minDate={formData.eventDate || formatDateLocal(new Date())}
                  error={!!errors.eventEndDate}
                  placeholder="Slutdatum"
                />
              </div>
            )}
            {!dateUndecided && !showEndDate && (
              <button
                type="button"
                onClick={() => {
                  markFormStarted();
                  setShowEndDate(true);
                }}
                className="mt-1 text-xs text-purple-400 hover:text-purple-300"
              >
                + Lägg till slutdatum
              </button>
            )}
            {errors.eventDate && (
              <p className="text-red-400 text-xs mt-0.5">{errors.eventDate}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-0.5">Ort</label>
            <input
              type="text"
              value={formData.customerCity}
              onChange={(e) => {
                markFormStarted();
                setFormData({ ...formData, customerCity: e.target.value });
              }}
              className={inputClass()}
              placeholder="T.ex. Malmö, Lund, Helsingborg"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-0.5">
              Venue / lokal om känd
            </label>
            <input
              type="text"
              value={formData.venue}
              onChange={(e) => {
                markFormStarted();
                setFormData({ ...formData, venue: e.target.value });
              }}
              className={inputClass()}
              placeholder="T.ex. konferenslokal, hotell eller arena"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-0.5">Antal gäster *</label>
            <input
              type="number"
              value={formData.guestCount}
              onChange={(e) => {
                markFormStarted();
                setFormData({ ...formData, guestCount: e.target.value });
              }}
              min="1"
              className={inputClass(!!errors.guestCount)}
              placeholder="T.ex. 120"
            />
            {errors.guestCount && (
              <p className="text-red-400 text-xs mt-0.5">{errors.guestCount}</p>
            )}
          </div>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold text-white mb-2">Vad behöver ni hjälp med?</h3>
        <div className="flex flex-wrap gap-1.5">
          {B2B_NEEDS.map((need) => {
            const selected = formData.needs.includes(need);
            return (
              <button
                key={need}
                type="button"
                onClick={() => toggleNeed(need)}
                className={`px-2.5 py-1 text-xs border transition-colors text-left ${
                  selected
                    ? 'border-purple-500 bg-purple-600/30 text-white'
                    : 'border-gray-600 text-gray-300 hover:border-gray-400'
                }`}
              >
                {need}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold text-white mb-2">Helhetsansvar</h3>
        <p className="text-xs text-gray-400 mb-2">Vill ni att vi tar helhetsansvar för tekniken?</p>
        <div className="flex flex-wrap gap-1.5">
          {B2B_FULL_SERVICE.map((option) => {
            const selected = formData.fullServicePreference === option;
            return (
              <button
                key={option}
                type="button"
                onClick={() => {
                  markFormStarted();
                  setFormData({
                    ...formData,
                    fullServicePreference: selected ? '' : option,
                  });
                }}
                className={`px-2.5 py-1 text-xs border transition-colors ${
                  selected
                    ? 'border-purple-500 bg-purple-600/30 text-white'
                    : 'border-gray-600 text-gray-300 hover:border-gray-400'
                }`}
              >
                {option}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold text-white mb-2">Budget</h3>
        <div className="flex flex-wrap gap-1.5">
          {B2B_BUDGET.map((segment) => {
            const selected = formData.budgetSegment === segment;
            return (
              <button
                key={segment}
                type="button"
                onClick={() => {
                  markFormStarted();
                  setFormData({
                    ...formData,
                    budgetSegment: selected ? '' : segment,
                  });
                }}
                className={`px-2.5 py-1 text-xs border transition-colors ${
                  selected
                    ? 'border-purple-500 bg-purple-600/30 text-white'
                    : 'border-gray-600 text-gray-300 hover:border-gray-400'
                }`}
              >
                {segment}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-bold text-white mb-2 flex items-center">
          <User className="mr-1.5" size={16} />
          Kontakt
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-0.5">Företag *</label>
            <input
              type="text"
              value={formData.companyName}
              onChange={(e) => {
                markFormStarted();
                setFormData({ ...formData, companyName: e.target.value });
              }}
              className={inputClass(!!errors.companyName)}
              placeholder="Företagsnamn"
              autoComplete="organization"
            />
            {errors.companyName && (
              <p className="text-red-400 text-xs mt-0.5">{errors.companyName}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-0.5">
              Kontaktperson *
            </label>
            <input
              type="text"
              value={formData.customerName}
              onChange={(e) => {
                markFormStarted();
                setFormData({ ...formData, customerName: e.target.value });
              }}
              className={inputClass(!!errors.customerName)}
              placeholder="Ditt namn"
              autoComplete="name"
            />
            {errors.customerName && (
              <p className="text-red-400 text-xs mt-0.5">{errors.customerName}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-0.5">E-post *</label>
            <input
              type="email"
              value={formData.customerEmail}
              onChange={(e) => {
                markFormStarted();
                setFormData({ ...formData, customerEmail: e.target.value });
              }}
              className={inputClass(!!errors.customerEmail)}
              placeholder="din@foretag.se"
              autoComplete="email"
            />
            {errors.customerEmail && (
              <p className="text-red-400 text-xs mt-0.5">{errors.customerEmail}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-0.5">Telefon</label>
            <input
              type="tel"
              value={formData.customerPhone}
              onChange={(e) => {
                markFormStarted();
                setFormData({ ...formData, customerPhone: e.target.value });
              }}
              className={inputClass(!!errors.customerPhone)}
              placeholder="070-123 45 67"
              autoComplete="tel"
            />
            {errors.customerPhone && (
              <p className="text-red-400 text-xs mt-0.5">{errors.customerPhone}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-0.5 flex items-center">
              <MessageSquare className="mr-1.5" size={14} />
              Fritext
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => {
                markFormStarted();
                setFormData({ ...formData, message: e.target.value });
              }}
              className={inputClass()}
              rows={3}
              placeholder="Övrig information om eventet, tidsplan eller särskilda önskemål"
            />
          </div>
        </div>
      </div>

      {submitError && (
        <div className="mb-3 p-2.5 bg-red-500/20 border border-red-500/30 rounded-none">
          <p className="text-red-400 text-sm">{submitError}</p>
        </div>
      )}

      <div className="space-y-3">
        <p className="text-sm text-gray-400 text-center md:text-left">
          Ni behöver inte veta vilken utrustning som krävs. Beskriv eventet så hjälper vi er att
          dimensionera rätt lösning.
        </p>
        <p className="text-xs text-slate-500">Vi återkommer normalt inom 24 timmar.</p>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2 border-t border-slate-800">
          <a
            href="tel:+46766777232"
            className="inline-flex flex-col items-center sm:items-start text-center sm:text-left group"
          >
            <span className="text-xs text-slate-500 mb-1">Eller ring oss direkt</span>
            <span className="inline-flex items-center gap-2 text-2xl md:text-3xl font-bold text-white group-hover:text-purple-300 transition-colors">
              <Phone size={22} className="text-purple-400 shrink-0" />
              076-677 72 32
            </span>
          </a>
          <button
            type="submit"
            disabled={loading}
            className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-bold rounded-none transition-all duration-300 flex items-center justify-center disabled:opacity-50 shrink-0"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                Skickar...
              </>
            ) : (
              <>
                <Send size={18} className="mr-2" />
                Skicka förfrågan
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default B2bEventFormSections;
